import React, { useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { Invoice } from "../components/Invoice";
import { toast } from "sonner";
import {
  Laptop,
  Edit3,
  Trash2,
  Plus,
  ClipboardList,
  Receipt,
  CheckCircle2,
} from "lucide-react";

const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const StatusPill = ({ status }) => {
  const cls =
    status === "Completed"
      ? "chip-success"
      : status === "In-Progress"
      ? "chip-brand"
      : "chip-warn";
  return <span className={`chip ${cls}`}>{status}</span>;
};

export const AdminDashboard = () => {
  const {
    bookings,
    addExtraToBooking,
    removeExtraFromBooking,
    closeBookingWithToken,
    BASE_DIAGNOSTIC_RATE,
    INSPECTION_FEE,
  } = useApp();

  const [selectedId, setSelectedId] = useState(bookings[0]?.id || null);
  const [partName, setPartName] = useState("");
  const [partCost, setPartCost] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  const [filter, setFilter] = useState("all");

  const selected = useMemo(
    () => bookings.find((b) => b.id === selectedId) || null,
    [bookings, selectedId]
  );

  const filtered = useMemo(() => {
    if (filter === "all") return bookings;
    return bookings.filter((b) => b.status.toLowerCase() === filter);
  }, [bookings, filter]);

  const kpis = useMemo(() => {
    const total = bookings.length;
    const inProg = bookings.filter((b) => b.status === "In-Progress").length;
    const assigned = bookings.filter((b) => b.status === "Assigned").length;
    const paid = bookings.filter((b) => b.isPaid);
    const revenue = paid.reduce((s, b) => {
      const extras = b.extras.reduce((a, x) => a + Number(x.cost || 0), 0);
      return s + b.baseDiagnostic + b.inspectionFee + extras;
    }, 0);
    return { total, inProg, assigned, revenue };
  }, [bookings]);

  const addPart = (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Select a booking first");
    if (!partName.trim() || !partCost) return toast.error("Enter part name and cost");
    addExtraToBooking(selected.id, partName.trim(), Number(partCost));
    setPartName("");
    setPartCost("");
    toast.success("Item added to the bill");
  };

  const submitToken = (e) => {
    e.preventDefault();
    if (!selected) return;
    if (tokenInput.length !== 4) return toast.error("Enter the 4-digit handover code");
    const ok = closeBookingWithToken(selected.id, tokenInput);
    if (ok) {
      toast.success(`${selected.id} settled · invoice generated`);
      setTokenInput("");
    } else {
      toast.error("Handover code mismatch");
    }
  };

  const extrasTotal = selected
    ? selected.extras.reduce((s, x) => s + Number(x.cost || 0), 0)
    : 0;
  const grand = selected ? selected.baseDiagnostic + selected.inspectionFee + extrasTotal : 0;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8">
      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-[var(--brand-deep)] font-semibold">
            Admin dashboard
          </div>
          <h1 className="font-display text-4xl text-[var(--ink-1)] mt-1">
            Operations <span className="font-serif-italic text-[var(--brand)]">control panel</span>
          </h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Manage orders, build bills, and settle repairs with handover codes.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { k: kpis.total, v: "Total orders" },
            { k: kpis.assigned, v: "Assigned" },
            { k: kpis.inProg, v: "In progress" },
            { k: money(kpis.revenue), v: "Revenue settled" },
          ].map((s) => (
            <div key={s.v} className="card p-4 min-w-[140px]">
              <div className="font-display text-2xl text-[var(--ink-1)]">{s.k}</div>
              <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] mt-0.5">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid xl:grid-cols-12 gap-6">
        <div className="xl:col-span-7">
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <ClipboardList size={16} className="text-[var(--brand)]" />
                <div className="font-display text-lg text-[var(--ink-1)]">Active orders</div>
              </div>
              <div className="flex items-center gap-2">
                {["all", "assigned", "in-progress", "completed"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={
                      "text-xs px-3 py-1.5 rounded-full border transition-colors capitalize " +
                      (filter === f
                        ? "border-[var(--brand)] text-[var(--brand-deep)] bg-[var(--brand-soft)]"
                        : "border-[var(--line-strong)] text-[var(--ink-2)] hover:border-[var(--ink-3)]")
                    }
                    data-testid={`admin-filter-${f}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">
                    <th className="text-left py-3 px-3">Order</th>
                    <th className="text-left py-3 px-3">Device</th>
                    <th className="text-left py-3 px-3">Gate code</th>
                    <th className="text-left py-3 px-3">Status</th>
                    <th className="text-right py-3 px-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--line)]">
                  {filtered.map((b) => (
                    <tr
                      key={b.id}
                      className={
                        "transition-colors " +
                        (selectedId === b.id
                          ? "bg-[var(--brand-soft)]/60"
                          : "hover:bg-[var(--bg-soft)]")
                      }
                      data-testid={`admin-order-row-${b.id}`}
                    >
                      <td className="py-3 px-3 font-semibold text-[var(--ink-1)]">{b.id}</td>
                      <td className="py-3 px-3">
                        <div className="text-[var(--ink-1)] font-medium">{b.brand}</div>
                        <div className="text-xs text-[var(--ink-3)]">{b.model}</div>
                        <div className="text-xs text-[var(--ink-4)] mt-0.5">+91 {b.phone}</div>
                      </td>
                      <td className="py-3 px-3">
                        <span className="tracking-widest font-semibold text-[var(--brand-deep)]">
                          {b.gateOTP}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <StatusPill status={b.status} />
                      </td>
                      <td className="py-3 px-3 text-right">
                        <button
                          onClick={() => setSelectedId(b.id)}
                          className="btn-secondary"
                          data-testid={`admin-edit-bill-${b.id}`}
                        >
                          <Edit3 size={12} /> Edit bill
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-[var(--ink-3)] text-sm">
                        No orders in this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="xl:col-span-5 space-y-6">
          <div className="card p-6" data-testid="admin-bill-workspace">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Receipt size={16} className="text-[var(--brand)]" />
                <div className="font-display text-lg text-[var(--ink-1)]">Bill maker</div>
              </div>
              {selected ? (
                <span className="chip chip-brand">{selected.id}</span>
              ) : (
                <span className="chip chip-slate">No selection</span>
              )}
            </div>

            {!selected ? (
              <div className="text-sm text-[var(--ink-2)] py-8 text-center">
                Select an order from the table to load its bill workspace.
              </div>
            ) : selected.status === "Completed" ? (
              <div className="text-center py-6">
                <CheckCircle2 size={22} className="mx-auto text-[var(--success)]" />
                <div className="text-sm text-[var(--ink-1)] mt-2 font-medium">
                  This order has been settled.
                </div>
                <div className="text-xs text-[var(--ink-3)] mt-1">Invoice shown below.</div>
              </div>
            ) : (
              <>
                <div className="card-soft p-4 rounded-2xl">
                  <div className="flex items-center gap-2 text-sm text-[var(--ink-1)]">
                    <Laptop size={14} className="text-[var(--brand)]" />
                    <span className="font-semibold">{selected.brand}</span>
                    <span className="text-[var(--ink-3)]">·</span>
                    <span>{selected.model}</span>
                  </div>
                  <div className="text-xs text-[var(--ink-3)] mt-1">
                    +91 {selected.phone} · {selected.address}
                  </div>
                </div>

                <form onSubmit={addPart} className="mt-4 grid grid-cols-12 gap-2">
                  <input
                    className="field col-span-7"
                    placeholder="Part / extra service"
                    value={partName}
                    onChange={(e) => setPartName(e.target.value)}
                    data-testid="admin-part-name-input"
                  />
                  <input
                    className="field col-span-3"
                    placeholder="₹ cost"
                    type="number"
                    min="0"
                    value={partCost}
                    onChange={(e) => setPartCost(e.target.value)}
                    data-testid="admin-part-cost-input"
                  />
                  <button
                    className="btn-primary col-span-2 justify-center"
                    data-testid="admin-add-part-button"
                  >
                    <Plus size={14} />
                  </button>
                </form>

                <div className="mt-4 space-y-2">
                  {selected.extras.length === 0 && (
                    <div className="text-xs text-[var(--ink-3)] text-center py-3">
                      No extras added yet
                    </div>
                  )}
                  {selected.extras.map((x) => (
                    <div
                      key={x.id}
                      className="flex items-center justify-between rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"
                      data-testid={`admin-extra-row-${x.id}`}
                    >
                      <div>
                        <div className="text-[var(--ink-1)] font-medium">{x.name}</div>
                        <div className="text-xs text-[var(--ink-3)]">Custom line item</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="font-semibold text-[var(--ink-1)]">{money(x.cost)}</div>
                        <button
                          type="button"
                          onClick={() => removeExtraFromBooking(selected.id, x.id)}
                          className="text-[var(--ink-4)] hover:text-red-600"
                          data-testid={`admin-remove-extra-${x.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 card-soft rounded-2xl p-4">
                  <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold mb-3">
                    Live bill summary
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <Row k="Base diagnostic rate" v={money(BASE_DIAGNOSTIC_RATE)} />
                    <Row k="Doorstep inspection fee" v={money(INSPECTION_FEE)} />
                    <Row k="Parts & extra services" v={money(extrasTotal)} />
                  </div>
                  <div className="divider-dashed my-3" />
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-[var(--ink-2)] font-semibold">
                      Grand total
                    </div>
                    <div className="font-display text-3xl text-[var(--brand-deep)]">
                      {money(grand)}
                    </div>
                  </div>
                </div>

                <form onSubmit={submitToken} className="mt-5 space-y-2">
                  <label className="text-sm font-medium text-[var(--ink-1)] block">
                    Customer handover code
                  </label>
                  <div className="flex gap-2">
                    <input
                      className="field field-mono"
                      value={tokenInput}
                      onChange={(e) =>
                        setTokenInput(e.target.value.replace(/\D/g, "").slice(0, 4))
                      }
                      placeholder="• • • •"
                      maxLength={4}
                      inputMode="numeric"
                      data-testid="admin-token-input"
                    />
                    <button
                      className="btn-success whitespace-nowrap"
                      data-testid="admin-done-button"
                    >
                      Settle order
                    </button>
                  </div>
                  <div className="text-xs text-[var(--ink-3)]">
                    Admin hint · code for this order ·{" "}
                    <span className="font-semibold text-[var(--brand-deep)]">
                      {selected.handoverToken}
                    </span>
                  </div>
                </form>
              </>
            )}
          </div>

          {selected && selected.status === "Completed" && (
            <div>
              <Invoice booking={selected} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between">
    <div className="text-[var(--ink-2)]">{k}</div>
    <div className="font-semibold text-[var(--ink-1)]">{v}</div>
  </div>
);
